"use client";

import { ContentfulImage } from "@app/_components/ContentfulImage";
import useTranslate from "@app/_hooks/useTranslate";
import type { ProductPageInfoFragment } from "@server/gql/graphql";
import { ArrowRight as ArrowIcon } from "lucide-react";
import Link from "next/link";

import { cn } from "@/lib/utils";
import { extractHrefToSlugWithLinkParams } from "@/utils/extractHrefToSlugWithLinkParams";

export default function ProductPageInteriorDesignService({
  className,
  contentfulProductPageInfo,
}: {
  className?: string;
  contentfulProductPageInfo?: ProductPageInfoFragment;
}) {
  const interiorDesignService =
    contentfulProductPageInfo?.interiorDesignService;

  if (!interiorDesignService?.header && !interiorDesignService?.subheading) {
    return null;
  }

  return (
    <div
      className={cn(
        "border-gray-150 flex flex-col border text-base",
        className,
      )}
    >
      {/* <InteriorDesignServiceImage
        interiorDesignService={interiorDesignService}
      /> */}
      <InteriorDesignServiceContent
        interiorDesignService={interiorDesignService}
      />
    </div>
  );
}

function InteriorDesignServiceImage({
  interiorDesignService,
}: {
  interiorDesignService: ProductPageInfoFragment["interiorDesignService"];
}) {
  if (!interiorDesignService) {
    return null;
  }

  return (
    <ContentfulImage
      className="bg-gray-150 aspect-[5/3] w-full shrink-0"
      desktopImage={interiorDesignService.desktopImage}
      mobileImage={interiorDesignService.mobileImage}
      sizes="(min-width: 1024px) 150px, 100vw"
    />
  );
}

function InteriorDesignServiceContent({
  interiorDesignService,
}: {
  interiorDesignService: ProductPageInfoFragment["interiorDesignService"];
}) {
  const t = useTranslate();
  if (!interiorDesignService) {
    return null;
  }

  const navigationLink = interiorDesignService?.href;
  const href = extractHrefToSlugWithLinkParams(navigationLink);

  return (
    <div className="flex grow flex-col gap-4 p-4">
      {(interiorDesignService.header || t("doYouNeedInteriorDesignHelp")) && (
        <h3 className="all-small-caps text-base font-normal tracking-wider uppercase">
          {interiorDesignService.header ?? t("doYouNeedInteriorDesignHelp")}
        </h3>
      )}
      {interiorDesignService.subheading && (
        <p>{interiorDesignService.subheading}</p>
      )}
      {href && (
        <Link
          href={href}
          className="all-small-caps group bg-primary hover:bg-primary-400 mt-2 flex flex-row flex-nowrap items-center gap-1 self-start px-4 py-2 tracking-wider text-black uppercase transition-colors duration-150"
        >
          {interiorDesignService.linkText ?? t("bookAFreeConsultation")}
          <ArrowIcon
            strokeWidth={1}
            className="size-4 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5"
          />
        </Link>
      )}
    </div>
  );
}
